import { useMemo } from 'react';

import {
  panelManagementLabels,
  usePanelManagementNavigationItems,
} from '@/components';
import { ROUTES } from '@/constants';

interface NavItem {
  label: string;
  onClick: () => void;
  isActiveItem: boolean;
  position: number;
}

interface NavigationItemsParams {
  selectedItem: string | null;
  handleNavigation: (href: string, label: string) => void;
  panelId: string | undefined;
  companyId: string | undefined;
  permissions: {
    hasPermissionToViewPanel: boolean;
    hasPermissionToViewPaymentMethods: boolean;
    hasPermissionToViewPaymentMethodCodes: boolean;
    hasPermissionToViewBulkBan: boolean;
    hasPermissionToViewBulkBalanceAdjust: boolean;
    hasPermissionToViewEmailBlocklist: boolean;
    hasPermissionToViewBuyerIdBlocklist: boolean;
    isBuyerBlockingEnabled: boolean;
    hasPermissionToPanelistImport: boolean;
    hasPermissionToLeadImport: boolean;
    hasPermissionToManualActivation: boolean;
    hasPermissionToViewMarketingCampaigns: boolean;
    isReferralProgramEnabled: boolean;
    hasPermissionToViewProfiling: boolean;
  };
}

export const NavigationItems = ({
  selectedItem,
  handleNavigation,
  panelId,
  companyId,
  permissions,
}: NavigationItemsParams) => {
  const {
    hasPermissionToViewPanel,
    hasPermissionToViewPaymentMethods,
    hasPermissionToViewPaymentMethodCodes,
    hasPermissionToViewBulkBan,
    hasPermissionToViewBulkBalanceAdjust,
    hasPermissionToViewEmailBlocklist,
    hasPermissionToViewBuyerIdBlocklist,
    isBuyerBlockingEnabled,
    hasPermissionToPanelistImport,
    hasPermissionToLeadImport,
    hasPermissionToManualActivation,
    hasPermissionToViewMarketingCampaigns,
    isReferralProgramEnabled,
    hasPermissionToViewProfiling,
  } = permissions;

  const { panelManagementItems } = usePanelManagementNavigationItems({
    selectedItem,
    handleNavigation,
    panelId,
    companyId,
    hasPermissionToViewPanel,
    hasPermissionToPanelistImport,
    hasPermissionToLeadImport,
    hasPermissionToManualActivation,
    hasPermissionToViewMarketingCampaigns,
    isReferralProgramEnabled,
    hasPermissionToViewProfiling,
  });

  const rewardsItems = useMemo<NavItem[]>(() => {
    const items: NavItem[] = [];
    if (hasPermissionToViewPaymentMethods)
      items.push({
        label: 'Redemption methods',
        onClick: () =>
          handleNavigation(ROUTES.paymentMethods, 'Redemption methods'),
        isActiveItem: selectedItem === 'Redemption methods',
        position: items.length,
      });
    if (hasPermissionToViewPaymentMethodCodes)
      items.push({
        label: 'Voucher code management',
        onClick: () =>
          handleNavigation(ROUTES.voucherCode, 'Voucher code management'),
        isActiveItem: selectedItem === 'Voucher code management',
        position: items.length,
      });
    return items;
  }, [
    hasPermissionToViewPaymentMethods,
    hasPermissionToViewPaymentMethodCodes,
    selectedItem,
    handleNavigation,
  ]);

  const trustAndSafetyItems = useMemo<NavItem[]>(() => {
    const items: NavItem[] = [];
    if (hasPermissionToViewBulkBan)
      items.push({
        label: 'Bulk banning',
        onClick: () => handleNavigation(ROUTES.bulkBanning, 'Bulk banning'),
        isActiveItem: selectedItem === 'Bulk banning',
        position: items.length,
      });
    if (hasPermissionToViewBulkBalanceAdjust)
      items.push({
        label: 'Bulk balance adjustment',
        onClick: () =>
          handleNavigation(
            ROUTES.bulkBalanceAdjustment,
            'Bulk balance adjustment'
          ),
        isActiveItem: selectedItem === 'Bulk balance adjustment',
        position: items.length,
      });
    if (hasPermissionToViewEmailBlocklist)
      items.push({
        label: 'Email domain blocklist',
        onClick: () =>
          handleNavigation(ROUTES.emailBlocklist, 'Email domain blocklist'),
        isActiveItem: selectedItem === 'Email domain blocklist',
        position: items.length,
      });
    if (hasPermissionToViewBuyerIdBlocklist && isBuyerBlockingEnabled)
      items.push({
        label: 'Buyer ID blocklist',
        onClick: () =>
          handleNavigation(ROUTES.buyerIdBlocklist, 'Buyer ID blocklist'),
        isActiveItem: selectedItem === 'Buyer ID blocklist',
        position: items.length,
      });
    return items;
  }, [
    hasPermissionToViewBulkBan,
    hasPermissionToViewBulkBalanceAdjust,
    hasPermissionToViewEmailBlocklist,
    hasPermissionToViewBuyerIdBlocklist,
    isBuyerBlockingEnabled,
    selectedItem,
    handleNavigation,
  ]);

  const analyticsItems = useMemo<NavItem[]>(
    () => [
      {
        label: 'Recruitment insights',
        onClick: () =>
          handleNavigation(ROUTES.recruitmentInsights, 'Recruitment insights'),
        isActiveItem: selectedItem === 'Recruitment insights',
        position: 0,
      },
      {
        label: 'Panel insights',
        onClick: () =>
          handleNavigation(ROUTES.overviewInsights, 'Panel insights'),
        isActiveItem: selectedItem === 'Panel insights',
        position: 1,
      },
      {
        label: 'Billing & Invoicing',
        onClick: () =>
          handleNavigation(ROUTES.billingInsights, 'Billing & Invoicing'),
        isActiveItem: selectedItem === 'Billing & Invoicing',
        position: 2,
      },
    ],
    [selectedItem, handleNavigation]
  );

  const rewardsLabels = useMemo(
    () => rewardsItems.map((item) => item.label),
    [rewardsItems]
  );

  const trustAndSafetyLabels = useMemo(
    () => trustAndSafetyItems.map((item) => item.label),
    [trustAndSafetyItems]
  );

  const analyticsLabels = useMemo(
    () => analyticsItems.map((item) => item.label),
    [analyticsItems]
  );

  return {
    panelManagementItems,
    rewardsItems,
    trustAndSafetyItems,
    analyticsItems,
    panelManagementLabels,
    rewardsLabels,
    trustAndSafetyLabels,
    analyticsLabels,
  };
};
